import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { Icon } from '@gitbook/spine-icons';
import { Button, type ButtonKind, type ButtonSize, DialogRoot } from '@gitbook/spine-react';

import { UpgradeDialogProvider } from '../UpgradeDialog';
import { useSitePublish } from './useSitePublish';

const SITE_ADMIN_ACCESS_MESSAGE = 'Only site administrators can manage site access';
const SITE_ADMIN_PUBLISH_MESSAGE = 'Only site administrators can publish this site';

export function SitePublishButton(props: {
    site: api.Site;
    organization: api.Organization;
    canAdminSite: boolean;
    onPress?: () => void;
    /** Called once the site is published. */
    onPublished?: (site: api.Site) => void;
    kind?: ButtonKind;
    size?: ButtonSize;
}) {
    return (
        <UpgradeDialogProvider>
            <SitePublishButtonContent {...props} />
        </UpgradeDialogProvider>
    );
}

function SitePublishButtonContent(props: {
    site: api.Site;
    organization: api.Organization;
    canAdminSite: boolean;
    onPress?: () => void;
    onPublished?: (site: api.Site) => void;
    kind?: ButtonKind;
    size?: ButtonSize;
}) {
    const { site, organization, canAdminSite, onPress, onPublished, kind, size } = props;
    const publish = useSitePublish({ organization, onPress, onPublished });

    const buttonProps = getSitePublishButtonProps({
        site,
        organization,
        canAdminSite,
        onPressPublish: publish.publishSite,
    });

    return (
        <DialogRoot {...publish.dialogRootProps}>
            <Button {...buttonProps} kind={kind} size={size} />
            {publish.publishDialogElement}
        </DialogRoot>
    );
}

function getSitePublishButtonProps(props: {
    site: api.Site;
    organization: api.Organization;
    canAdminSite: boolean;
    onPressPublish: (site: api.Site) => Promise<void>;
}) {
    const { site, organization, canAdminSite, onPressPublish } = props;

    if (site.published) {
        if (
            [api.SiteVisibility.Public, api.SiteVisibility.Unlisted].includes(site.visibility) &&
            site.urls.published
        ) {
            return {
                label: 'Visit',
                leadingIcon: Icon.ExternalLink,
                href: site.urls.published || '',
                target: '_blank',
                onPress: () => {},
            };
        }

        if (site.visibility === api.SiteVisibility.ShareLink) {
            if (!canAdminSite) {
                return {
                    label: 'Share links',
                    leadingIcon: Icon.PublishStatusShareLink,
                    disabled: true,
                    title: SITE_ADMIN_ACCESS_MESSAGE,
                };
            }

            return {
                label: 'Share links',
                leadingIcon: Icon.PublishStatusShareLink,
                href: {
                    overlay: null,
                    screen: {
                        type: 'site' as const,
                        organization: organization.id,
                        site: site.id,
                        tab: {
                            type: 'settings' as const,
                            section: 'audience' as const,
                        },
                    },
                },
                onPress: () => {
                    analytics.logEvent(
                        analytics.createSiteEvent(
                            { id: site.id, organization: organization.id },
                            { type: 'site_manage_share_links', triggerLocation: 'header' }
                        )
                    );
                },
            };
        }

        if (site.visibility === api.SiteVisibility.VisitorAuth) {
            if (!canAdminSite) {
                return {
                    label: 'Authenticated access',
                    leadingIcon: Icon.PublishStatusVisitorAuth,
                    disabled: true,
                    title: SITE_ADMIN_ACCESS_MESSAGE,
                };
            }

            return {
                label: 'Authenticated access',
                leadingIcon: Icon.PublishStatusVisitorAuth,
                href: {
                    overlay: null,
                    screen: {
                        type: 'site' as const,
                        organization: organization.id,
                        site: site.id,
                        tab: {
                            type: 'settings' as const,
                            section: 'audience' as const,
                        },
                    },
                },
                onPress: () => {
                    analytics.logEvent(
                        analytics.createSiteEvent(
                            { id: site.id, organization: organization.id },
                            {
                                type: 'site_manage_authenticated_access',
                                triggerLocation: 'header',
                            }
                        )
                    );
                },
            };
        }
    }

    // Not published - show publish button
    const notPublishableReason = getSiteNotPublishableReason({ site, canAdminSite });

    return {
        label: 'Publish',
        leadingIcon: Icon.Publish,
        disabled: Boolean(notPublishableReason),
        title: notPublishableReason ?? undefined,
        loadable: true,
        onPress: async () => {
            await onPressPublish(site);
        },
    };
}

/**
 * Get the reason why a site cannot be published by the current user.
 */
export function getSiteNotPublishableReason(props: {
    site: api.Site;
    canAdminSite: boolean;
}): string | null {
    const { canAdminSite, site } = props;

    if (!canAdminSite) {
        return SITE_ADMIN_PUBLISH_MESSAGE;
    }
    if (site.siteSpaces <= 0) {
        return 'Link a space to publish this site';
    }
    return null;
}
